import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Headphones, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = ({ proactiveDelay = 30000 }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasShownProactive, setHasShownProactive] = useState(false);
  const [showProactiveHint, setShowProactiveHint] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! 👋 I'm your CLOUD IT assistant. I can help you with:\n\n• Information about our services & pricing\n• Your order status & project updates\n• General questions about CLOUD IT\n\nHow can I help you today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketData, setTicketData] = useState({ subject: '', message: '' });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const proactiveTimerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Proactive chatbot trigger after 30 seconds
  useEffect(() => {
    const hasSeenProactive = sessionStorage.getItem('chatbot_proactive_shown');
    
    if (!hasSeenProactive && !isOpen && !hasShownProactive) {
      proactiveTimerRef.current = setTimeout(() => {
        setShowProactiveHint(true);
        setHasShownProactive(true);
        sessionStorage.setItem('chatbot_proactive_shown', 'true');
      }, proactiveDelay);
    }

    return () => {
      if (proactiveTimerRef.current) {
        clearTimeout(proactiveTimerRef.current);
      }
    };
  }, [isOpen, hasShownProactive, proactiveDelay]);

  // Hide proactive hint when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowProactiveHint(false);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/chatbot/message`,
        { 
          message: userMessage,
          user_id: user?.id || null
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const { reply, needs_escalation } = response.data;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: reply,
        needsEscalation: needs_escalation
      }]);

      if (needs_escalation) {
        setTimeout(() => {
          setShowTicketForm(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again or contact our support team directly.",
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketData.subject.trim() || !ticketData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API}/support/ticket`,
        {
          subject: ticketData.subject,
          message: ticketData.message,
          user_email: user?.email || 'anonymous',
          user_name: user?.name || 'Guest'
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      toast.success('Support ticket submitted! We\'ll get back to you soon.');
      setShowTicketForm(false);
      setTicketData({ subject: '', message: '' });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '✅ Your support ticket has been submitted successfully! Our team will review it and get back to you within 24 hours. Is there anything else I can help you with?'
      }]);
    } catch (error) {
      toast.error('Failed to submit ticket. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const sendQuickMessage = async (message) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/chatbot/message`,
        { 
          message: message,
          user_id: user?.id || null
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const { reply, needs_escalation } = response.data;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: reply,
        needsEscalation: needs_escalation
      }]);

      if (needs_escalation) {
        setTimeout(() => {
          setShowTicketForm(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again or contact our support team directly.",
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissProactiveHint = () => {
    setShowProactiveHint(false);
  };

  return (
    <>
      {/* Proactive Hint Bubble */}
      {showProactiveHint && !isOpen && (
        <div 
          className="fixed bottom-40 right-6 z-50 max-w-[280px] animate-fade-in-up"
          data-testid="proactive-hint"
        >
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-4 shadow-2xl relative">
            {/* Close button */}
            <button
              onClick={dismissProactiveHint}
              className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X size={12} />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
                <Sparkles size={18} className="text-white" />
              </div>
              <div>
                <p className="text-[var(--text-primary)] text-sm font-medium mb-1">
                  Need help deciding?
                </p>
                <p className="text-[var(--text-muted)] text-xs mb-3">
                  I can help you find the perfect service for your needs!
                </p>
                <button
                  onClick={() => setIsOpen(true)}
                  className="text-xs font-semibold text-white px-3 py-1.5 rounded-lg transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                >
                  Chat with me
                </button>
              </div>
            </div>
            
            {/* Arrow pointing to chat button */}
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-[var(--bg-card)] border-r border-b border-[var(--border)] transform rotate-45"></div>
          </div>
        </div>
      )}

      {/* Chat Toggle Button */}
      <button
        data-testid="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-2xl transition-all duration-300 flex items-center justify-center ${
          isOpen ? 'bg-white/10 backdrop-blur-xl border border-white/20' : ''
        } ${showProactiveHint && !isOpen ? 'animate-pulse' : ''}`}
        style={!isOpen ? { background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' } : {}}
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <MessageCircle size={24} className="text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chatbot-window"
          className="fixed bottom-40 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ height: '500px', maxHeight: 'calc(100vh - 150px)' }}
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)]" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Bot size={22} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">CLOUD IT Assistant</h3>
                <p className="text-white/70 text-xs">Powered by AI • Usually replies instantly</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' 
                    ? 'bg-[#F97316]/20 border border-[#F97316]/30' 
                    : msg.isError 
                      ? 'bg-red-500/20 border border-red-500/30'
                      : 'bg-[#3B82F6]/20 border border-[#3B82F6]/30'
                }`}>
                  {msg.role === 'user' ? (
                    <User size={16} className="text-[#F97316]" />
                  ) : msg.isError ? (
                    <AlertCircle size={16} className="text-red-400" />
                  ) : (
                    <Bot size={16} className="text-[#3B82F6]" />
                  )}
                </div>

                {/* Message */}
                <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white rounded-tr-md'
                    : msg.isError
                      ? 'bg-red-500/10 border border-red-500/20 text-red-300 rounded-tl-md'
                      : 'bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] rounded-tl-md'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  
                  {msg.needsEscalation && !showTicketForm && (
                    <button
                      onClick={() => setShowTicketForm(true)}
                      className="mt-3 flex items-center gap-2 text-[#F97316] hover:text-[#FB923C] transition-colors text-xs font-medium"
                    >
                      <Headphones size={14} />
                      Talk to an Agent
                    </button>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/20 border border-[#3B82F6]/30 flex items-center justify-center">
                  <Bot size={16} className="text-[#3B82F6]" />
                </div>
                <div className="bg-[var(--bg-elevated)] border border-[var(--border)] px-4 py-3 rounded-2xl rounded-tl-md">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="text-[#3B82F6] animate-spin" />
                    <span className="text-[var(--text-muted)] text-sm">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Ticket Form */}
            {showTicketForm && (
              <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-[#F97316] font-medium text-sm">
                  <Headphones size={16} />
                  Create Support Ticket
                </div>
                <input
                  type="text"
                  placeholder="Subject"
                  value={ticketData.subject}
                  onChange={(e) => setTicketData({ ...ticketData, subject: e.target.value })}
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-lg px-3 py-2 text-[var(--text-primary)] text-sm focus:outline-none focus:border-[#F97316]"
                />
                <textarea
                  placeholder="Describe your issue..."
                  value={ticketData.message}
                  onChange={(e) => setTicketData({ ...ticketData, message: e.target.value })}
                  rows={3}
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-lg px-3 py-2 text-[var(--text-primary)] text-sm focus:outline-none focus:border-[#F97316] resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowTicketForm(false)}
                    className="flex-1 py-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)] rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitTicket}
                    className="flex-1 py-2 text-sm font-semibold text-white rounded-lg"
                    style={{ background: 'linear-gradient(135deg, #F97316, #FB923C)' }}
                  >
                    Submit Ticket
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Action Buttons */}
          {messages.length <= 2 && !isLoading && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const msg = 'I need a quote for a new project';
                  setMessages(prev => [...prev, { role: 'user', content: msg }]);
                  sendQuickMessage(msg);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 rounded-full hover:bg-[#10B981]/20 transition-colors"
              >
                Get a Quote
              </button>
              <button
                onClick={() => {
                  const msg = 'What services do you offer?';
                  setMessages(prev => [...prev, { role: 'user', content: msg }]);
                  sendQuickMessage(msg);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30 rounded-full hover:bg-[#3B82F6]/20 transition-colors"
              >
                View Services
              </button>
              <button
                onClick={() => {
                  const msg = 'How much does a website cost?';
                  setMessages(prev => [...prev, { role: 'user', content: msg }]);
                  sendQuickMessage(msg);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/30 rounded-full hover:bg-[#8B5CF6]/20 transition-colors"
              >
                Pricing Info
              </button>
              <button
                onClick={() => {
                  const msg = 'I want to book a free consultation';
                  setMessages(prev => [...prev, { role: 'user', content: msg }]);
                  sendQuickMessage(msg);
                }}
                className="px-3 py-1.5 text-xs font-medium bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/30 rounded-full hover:bg-[#F97316]/20 transition-colors"
              >
                Book Consultation
              </button>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-[var(--border)]">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1 bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] text-sm focus:outline-none focus:border-[#3B82F6] disabled:opacity-50"
              />
              <button
                data-testid="chatbot-send"
                onClick={handleSendMessage}
                disabled={isLoading || !input.trim()}
                className="px-4 rounded-xl transition-all disabled:opacity-50 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                <Send size={18} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
